/**
 * Lightweight proxy-based read/write tracker used to wire fine-grained reactivity over
 * MvvmContextState without leaving the imperative API behind. The proxy is *not* a value
 * type — it forwards every operation to the raw underlying object — so callers can treat
 * `context.state.x` as if it were a plain object while we record the paths they touch.
 *
 * Two distinct use sites:
 *   1. Per-component read tracking. `onRead` records paths a component depends on this
 *      render; the binding reconciles those into actual subscriptions in a useEffect.
 *   2. Per-update write tracking. `onWrite` records paths mutated inside `update()` so the
 *      context can notify exactly the subscribers that need to re-render.
 *
 * Recording rule: only LEAF reads (primitives, null, and non-plain object value types)
 * call onRead. Intermediate plain-object/array traversals return nested proxies without
 * recording, so reading `state.vehicle.plate` subscribes to `vehicle.plate` only — not
 * to the entire `vehicle` subtree.
 */

import {deepEquals} from "@2d-front-monorepo/core";

export interface TrackingProxyOptions {
	onRead?: (path: string) => void;
	// Fired for intermediate plain-object/array reads (which return nested proxies without
	// hitting onRead). Consumers subscribe these replacement-only: notified when the object
	// at the path is replaced, not when a leaf below it changes.
	onObjectRead?: (path: string) => void;
	onWrite?: (path: string, oldValue: unknown, newValue: unknown) => void;
}

function joinPath(basePath: string, prop: string): string {
	return basePath ? `${basePath}.${prop}` : prop;
}

function isTrackableObject(value: unknown): value is object {
	if (value === null || typeof value !== "object") return false;
	if (Array.isArray(value)) return true;

	const proto = Object.getPrototypeOf(value);
	return proto === Object.prototype || proto === null;
}

// Reverse map for every tracking proxy ever created (across all caches/contexts), used to
// unwrap proxies before they are stored back into the raw state.
const proxyToRaw = new WeakMap<object, object>();

function unwrapProxy(value: unknown): unknown {
	if (value === null || typeof value !== "object") return value;
	return proxyToRaw.get(value) ?? value;
}

export function createTrackingProxy<T extends object>(
	target: T,
	basePath: string,
	opts: TrackingProxyOptions,
	cache: WeakMap<object, Map<string, any>>
): T {
	// Keyed by object AND path: the same object can sit at different paths over time
	// (array element shifting after a splice, shared references) and a proxy minted for
	// the old path would record stale paths for reads and writes.
	let byPath = cache.get(target);
	const cached = byPath?.get(basePath);
	if (cached != null) return cached;

	const proxy = new Proxy(target as object, {
		get(obj: any, prop: string | symbol, receiver: any): any {
			const value = Reflect.get(obj, prop, receiver);
			if (typeof prop === "symbol") return value;
			const path = joinPath(basePath, prop);
			if (isTrackableObject(value)) {
				opts.onObjectRead?.(path);
				return createTrackingProxy(value, path, opts, cache);
			}
			// Method reads (items.map, vehicle.hasOwnProperty, equals probed by deepEquals) are
			// not data: recording them would mint a bogus subscription and a reader-cache entry
			// per method per render. The call itself still reads through the proxy (this === proxy).
			if (typeof value !== "function") {
				opts.onRead?.(path);
			}
			return value;
		},
		set(obj: any, prop: string | symbol, value: any, receiver: any): boolean {
			if (typeof prop === "symbol") {
				return Reflect.set(obj, prop, value, receiver);
			}
			// Array methods (splice, sort, reverse, unshift) read elements through the proxy —
			// receiving nested proxies — and write them back. Unwrap so the raw state never
			// holds tracking proxies (whose recorded base paths would be stale after the move).
			value = unwrapProxy(value);
			const oldLength = Array.isArray(obj) ? obj.length : undefined;
			const oldValue = Reflect.get(obj, prop, receiver);
			const result = Reflect.set(obj, prop, value, receiver);
			// Value equality, like setValue: an onChange handler storing an equal-but-new
			// instance (a DateOnly the server echoed back) must not read as a change.
			if (result && !deepEquals(oldValue, value)) {
				opts.onWrite?.(joinPath(basePath, prop), oldValue, value);
			}
			if (result && oldLength !== undefined && prop !== "length" && obj.length !== oldLength) {
				opts.onWrite?.(joinPath(basePath, "length"), oldLength, obj.length);
			}
			return result;
		},
		deleteProperty(obj: any, prop: string | symbol): boolean {
			if (typeof prop === "symbol") {
				return Reflect.deleteProperty(obj, prop);
			}
			const oldValue = Reflect.get(obj, prop);
			const result = Reflect.deleteProperty(obj, prop);
			if (result && oldValue !== undefined) {
				opts.onWrite?.(joinPath(basePath, prop), oldValue, undefined);
			}
			return result;
		}
	}) as T;

	if (byPath == null) {
		byPath = new Map();
		cache.set(target, byPath);
	}
	byPath.set(basePath, proxy);
	proxyToRaw.set(proxy as object, target);
	return proxy;
}

/**
 * Synthetic subscription channels for state that is not part of the `state` tree
 * (errors, exceptions, validation lifecycle, init lifecycle). Subscribers attach to
 * these by name; `MvvmContextState._notify` fires them whenever the underlying state
 * transitions.
 */
export const SyntheticChannel = {
	errors: "$errors",
	exceptions: "$exceptions",
	submitting: "$submitting",
	validating: "$validating",
	initStatus: "$initStatus",
	errorForPath: (path: string): string => `$err:${path}`,
	// Replacement-only channel for object-valued reads: fired when the object at the path
	// (or an ancestor subtree) is written, never by leaf writes below it.
	objectForPath: (path: string): string => `$obj:${path}`
} as const;
